import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'components/Link/Link';
import { connect } from 'react-redux';
import InViewObserver from 'components/InViewObserver';
import analyticsWrapper from 'helpers/analytics/analyticsWrapper';
import Image from '../../../../../components/Image';
import LoadableComponentLoader from '../../../../../components_v2/common/LoadableComponentLoader/LoadableComponentLoader';
import { TOI_LIVE_DOMAIN, TOI_IMAGE_DOMAIN } from '../../../../../constants';
import ShareButton from '../ShareButton/ShareButton';
import { msToTime } from '../../../../../utils/common';
import s from './GameCard.scss';

const GameCard = props => {
  const {
    widgetUsedOn,
    calledFrom,
    isMobile,
    gameType,
    isVertical,
    // isHowtoPlayVisible,
    gameCategoryConfigData,
    data,
    ga4Category,
  } = props;
  const componentData = props?.data ? props.data : '';
  const isServerSide = calledFrom === 'ssr_homepage';

  if (!componentData) {
    return null;
  }

  const fireGaOnClick = label => {
    analyticsWrapper('ga4', {
      event: 'user_click',
      type: `${gameType.replace(/-/g, '_')}_widget`,
      category: ga4Category || '',
      label: label || '',
    });
  };

  if (isServerSide) {
    return (
      <InViewObserver>
        <Link
          className={`${s.cardLink}`}
          // to={`${TOI_LIVE_DOMAIN}/games/${gameType}/today`}
          to={`${TOI_LIVE_DOMAIN}/games/${gameType}/today${
            !!widgetUsedOn && widgetUsedOn !== '' && widgetUsedOn === 'homepage'
              ? '?utm_source=toi_homepage&utm_medium=game_banner&utm_campaign=game_promotion'
              : ''
          }`}
          onClick={() => {
            fireGaOnClick('play_now');
          }}
        >
          <div
            className={`${s.gameCard} gameCard ${s.br16} ${
              isVertical ? s.horizontal : ''
            }`}
            style={{ background: componentData?.backgroundColor }}
          >
            <div className={s.topArea}>
              <div className={s.left}>
                <figure>
                  <Image
                    src={componentData?.imageUrl}
                    placeHolderSrc={`${TOI_IMAGE_DOMAIN}/photo/111258810.cms`}
                    alt={componentData?.title}
                    // lazyload
                    offsetVertical={200}
                  />
                </figure>
              </div>
              <div className={s.right}>
                <h2>{componentData?.title}</h2>
                <span>{componentData?.caption}</span>
              </div>
            </div>
            <div className={s.btnGroup}>
              <div className={`${s.btnPrimary} ${s.br8}`}>
                Play Now <i className={s.arrowRight} />
              </div>
              {isMobile ? (
                <div className={`${s.share} ${s.br8}`}>
                  <LoadableComponentLoader
                    loadableComponentName="SocialSharingMobile"
                    isParent={false}
                    url={`${TOI_LIVE_DOMAIN}/games/${gameType}/today?utm_source=social_share&utm_medium=social&utm_campaign=games`}
                    title={componentData?.gameType}
                    nativeIconClassName="nativeIcon"
                    via="@timesofindia"
                    isTablet={false}
                    hideSharingButtons
                    hideSharingNative
                    placeHolderLoaderClass="follow_us_loader"
                    hideFollow
                    gaOnClick={() => {
                      fireGaOnClick('share');
                    }}
                  />
                </div>
              ) : (
                <div className={`${s.share} ${s.br8}`}>
                  <ShareButton
                    nativeIconClassName="nativeIcon"
                    title={componentData?.gameType}
                    url={`${TOI_LIVE_DOMAIN}/games/${gameType}/today?utm_source=social_share&utm_medium=social&utm_campaign=games`}
                    gaOnClick={() => {
                      fireGaOnClick('share');
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </Link>
      </InViewObserver>
    );
  }

  const [imageUrl, setImageUrl] = useState('');
  const [subStatus, setSubStatus] = useState('');
  const [isFinished, setIsFinished] = useState(false);

  useEffect(
    () => {
      const gameStatuses =
        data && Array.isArray(data.gameStatus) ? data.gameStatus : [];
      const allFinished = gameStatuses.every(status => status.hasPlayed);
      const anyPaused = gameStatuses.some(status => status.hasPaused);
      const getLocalStorageKeyPrefix = gameTypeVar => {
        switch (gameTypeVar) {
          case 'sudoku':
            return 'tilsudoku';
          case 'location-guesser':
            return 'tillocationguesser';
          case 'loop-a-loop':
            return 'tilloopaloop';
          case 'whos-that':
            return 'tilwhoisthat';
          default:
            return 'tilstate';
        }
      };

      const anyPausedInLocalStorage = gameStatuses.some(status =>
        localStorage.getItem(
          `${getLocalStorageKeyPrefix(gameType)}:${status.gameId}`,
        ),
      );

      if (allFinished) {
        setImageUrl(gameCategoryConfigData?.listingIconUrl?.finishImageUrl);
        setIsFinished(true);
        const lastFinishedGame = gameStatuses.find(
          status => status.completionTimeinMills,
        );
        if (lastFinishedGame) {
          setSubStatus(msToTime(lastFinishedGame.completionTimeinMills));
        }
        if (!!gameType && gameType === 'location-guesser') {
          const tempguessPoints = gameStatuses.find(
            guessPoints => guessPoints.bestGuessPoints,
          );
          if (tempguessPoints) {
            setSubStatus(tempguessPoints.bestGuessPoints);
          }
        }
        if (!!gameType && gameType === 'whos-that') {
          const temptotalScore = gameStatuses.find(
            totalScore => totalScore.totalScore,
          );
          if (temptotalScore) {
            setSubStatus(temptotalScore.totalScore);
          }
        }
      } else if (anyPaused || anyPausedInLocalStorage) {
        setImageUrl(gameCategoryConfigData?.listingIconUrl?.pauseImageUrl);
        setSubStatus('paused');
      } else {
        setImageUrl(gameCategoryConfigData?.listingIconUrl?.imageUrl);
        setSubStatus('');
      }
    },
    [data],
  );

  const onView = () => {
    analyticsWrapper('ga4', {
      event: 'user_shown',
      type: `${gameType.replace(/-/g, '_')}_widget`,
      category: ga4Category || '',
    });
  };

  return (
    <InViewObserver inViewCallback={onView}>
      <Link
        className={`${s.cardLink}`}
        // to={`${TOI_LIVE_DOMAIN}/games/${gameType}/today`}
        to={`${TOI_LIVE_DOMAIN}/games/${gameType}/today${
          !!widgetUsedOn && widgetUsedOn !== '' && widgetUsedOn === 'homepage'
            ? '?utm_source=toi_homepage&utm_medium=game_banner&utm_campaign=game_promotion'
            : ''
        }`}
        onClick={() => {
          fireGaOnClick('play_now');
        }}
      >
        <div
          className={`${s.gameCard} ${s.br16} ${
            isVertical ? s.horizontal : ''
          }`}
          style={{ background: componentData.backgroundColor }}
        >
          {data &&
            data.streakInfo &&
            data.streakInfo.text && (
              <div className={s.gameStreak}>
                <div className={s.streakDay}>
                  <i className={s.smallFireIcon} />
                  {data.streakInfo.text}
                </div>
              </div>
            )}
          <div className={s.topArea}>
            <div className={s.left}>
              <figure>
                <Image
                  src={imageUrl}
                  placeHolderSrc={`${TOI_IMAGE_DOMAIN}/photo/111258810.cms`}
                  alt={componentData.title}
                  lazyload={
                    !(
                      (widgetUsedOn && widgetUsedOn === 'homepage') ||
                      calledFrom === 'more_games_congratulations' ||
                      calledFrom === 'game_listing'
                    )
                  }
                  offsetVertical={200}
                />
              </figure>
              {isFinished && <i className={s.greenTickIcon} />}
              {subStatus && <span className={s.gameStatus}>{subStatus}</span>}
            </div>
            <div className={s.right}>
              {!(
                widgetUsedOn &&
                widgetUsedOn !== '' &&
                widgetUsedOn === 'homepage'
              ) &&
                gameType === 'whos-that' && (
                  <strong
                    className={`${s.newTag} ${
                      gameType === 'whos-that' ? s.whosThatNewTag : ''
                    }`}
                  >
                    New
                  </strong>
                )}
              <h2>{componentData.title}</h2>
              <span>{componentData.caption}</span>
            </div>
          </div>
          <div className={s.btnGroup}>
            {/* {isHowtoPlayVisible && (
            <Link
              className={s.howtoplay}
              to={`${TOI_LIVE_DOMAIN}/games/${gameType}`}
            >
              How to Play
            </Link>
          )} */}
            {/* <Link
              className={`${s.btnPrimary} ${s.br8}`}
              to={`${TOI_LIVE_DOMAIN}/games/${gameType}/today`}
              onClick={() => {
                fireGaOnClick('play_now');
              }}
            > */}
            <div className={`${s.btnPrimary} ${s.br8}`}>
              Play Now <i className={s.arrowRight} />
            </div>
            {/* </Link> */}
            {isMobile ? (
              <div className={`${s.share} ${s.br8}`}>
                <LoadableComponentLoader
                  loadableComponentName="SocialSharingMobile"
                  isParent={false}
                  url={`${TOI_LIVE_DOMAIN}/games/${gameType}/today?utm_source=social_share&utm_medium=social&utm_campaign=games`}
                  title={componentData.gameType}
                  nativeIconClassName="nativeIcon"
                  via="@timesofindia"
                  isTablet={false}
                  hideSharingButtons
                  hideSharingNative
                  placeHolderLoaderClass="follow_us_loader"
                  hideFollow
                  gaOnClick={() => {
                    fireGaOnClick('share');
                  }}
                />
              </div>
            ) : (
              <div className={`${s.share} ${s.br8}`}>
                <ShareButton
                  nativeIconClassName="nativeIcon"
                  title={componentData.gameType}
                  url={`${TOI_LIVE_DOMAIN}/games/${gameType}/today?utm_source=social_share&utm_medium=social&utm_campaign=games`}
                  gaOnClick={() => {
                    fireGaOnClick('share');
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </Link>
    </InViewObserver>
  );
};

GameCard.propTypes = {
  isMobile: PropTypes.bool,
  isVertical: PropTypes.bool,
  gameCategoryConfigData: PropTypes.shape({}),
};

GameCard.defaultProps = {
  isMobile: false,
  isVertical: false,
  gameCategoryConfigData: {},
};
const mapStateToProps = state => ({
  isMobile: state.isMobile,
});
export default connect(mapStateToProps)(GameCard);
